/* US Boat Cover Finder — Brand → Model (base) → confirm-tiles → recommended
   covers with live, market-scoped pricing. Reads
   assets/us-boat-cover-finder-data.json (manufacturers[mfr] → rows[{baseModel,
   configLabel, year, model, boatType, length, beam, covers[{useCase, code,
   handle, title}]}]), fetches each product live by handle (Storefront API
   @inContext) and matches the exact variant by SKU so prices and variant ids
   are always correct for the current store/market. Initialises every .bcf--us
   instance on the page (never the AU .bcf finder — see boot() below). No
   dependencies. */
(function () {
  "use strict";

  // After an add, open the theme's cart drawer (it rebuilds the header count and
  // drawer contents itself) by triggering the header cart icon's own handler.
  // Without a drawer (cart page mode), just refresh the count and contents.
  function refreshCart() {
    var opener = document.querySelector(".openCartDrawer");
    if (opener && window.jQuery) { opener.click(); return; }
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (window.buildCart && window.jQuery) { window.buildCart(cart, false); return; }
        document.querySelectorAll("[data-cart-count]").forEach(function (n) {
          n.textContent = cart.item_count < 100 ? cart.item_count : "";
          n.style.display = cart.item_count > 0 ? "" : "none";
        });
      })
      .catch(function () {});
  }

  function init(root) {
    if (root.dataset.bcfReady) return;
    root.dataset.bcfReady = "1";

    var market = window.OS_MARKET || {};
    var token = root.dataset.token || "";
    var country = String(market.country || root.dataset.country || "AU")
      .toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) || "AU";
    var rootUrl = (market.root || root.dataset.rootUrl || "/").replace(/\/+$/, "") + "/";
    var apiVersion = root.dataset.apiVersion || "2024-10";
    var dataSrc = root.dataset.src;
    var enquireUrl = root.dataset.enquireUrl || "/pages/contact";
    var tileCtaLabel = root.dataset.tileCta || "Select this boat";
    var tileCtaSelectedLabel = root.dataset.tileCtaSelected || "Selected";

    var mfrSel = root.querySelector('[data-role="mfr"]');
    var mdlSel = root.querySelector('[data-role="model"]');
    var results = root.querySelector('[data-role="results"]');
    var emptyNote = root.querySelector('[data-role="empty"]');
    var tilesStep = root.querySelector('[data-role="tiles-step"]');
    var tilesWrap = root.querySelector('[data-role="tiles"]');

    var DESCRIPTIONS = {};
    var descEl = root.querySelector('[data-role="descriptions"]');
    if (descEl) { try { DESCRIPTIONS = JSON.parse(descEl.textContent) || {}; } catch (e) { DESCRIPTIONS = {}; } }

    // Per-cover-type highlights (callout / tick list / icon badges), keyed by group.
    var COVERTYPE = {};
    var ctEl = root.querySelector('[data-role="covertype"]');
    if (ctEl) { try { COVERTYPE = JSON.parse(ctEl.textContent) || {}; } catch (e) { COVERTYPE = {}; } }
    // Map of badge icon filename -> served asset URL (asset_url resolved in Liquid).
    var ICON_URLS = {};
    var iconEl = root.querySelector('[data-role="bcf-icons"]');
    if (iconEl) { try { ICON_URLS = JSON.parse(iconEl.textContent) || {}; } catch (e) { ICON_URLS = {}; } }
    // Per-cover-type corner badge image, keyed by group (asset_url resolved in Liquid).
    var BADGE_URLS = {};
    var badgeMapEl = root.querySelector('[data-role="bcf-badges"]');
    if (badgeMapEl) { try { BADGE_URLS = JSON.parse(badgeMapEl.textContent) || {}; } catch (e) { BADGE_URLS = {}; } }
    // Confirm-tile boat illustrations: filename -> served asset URL. The
    // filename for a row comes from assets/us-boat-illustrations.js (loaded
    // just before this file); a row it can't place just gets a text tile.
    var BOAT_ART = {};
    var artEl = root.querySelector('[data-role="bcf-boat-illustrations"]');
    if (artEl) { try { BOAT_ART = JSON.parse(artEl.textContent) || {}; } catch (e) { BOAT_ART = {}; } }
    var pickBoatArt = (typeof window !== "undefined" && window.OSUSBoatIllustration) || null;
    // Warranty badge, rendered once in Liquid and cloned onto each cover card.
    // Empty (or absent) outside the US, which is where the gate lives.
    var badgeTpl = root.querySelector('template[data-role="warranty-badge"]');
    var warrantyBadge = badgeTpl && badgeTpl.content && badgeTpl.content.firstElementChild;
    var COVER_GROUP = {
      "Suggested cover": "classic",
      "With trolling motor / thruster": "classic",
      "With bimini up": "classic",
      "Universal": "universal",
      "Heavy duty winter cover": "heavyduty",
      "Custom cover": "custom",
    };
    function coverContent(useCase) {
      var g = COVER_GROUP[useCase];
      return (g && COVERTYPE[g]) || null;
    }
    function splitLines(s) {
      return String(s || "").split(/\r?\n/).map(function (x) { return x.trim(); }).filter(Boolean);
    }

    var DATA = null;
    var productCache = {}; // handle -> Promise<product|null>

    function apiUrl() { return "/api/" + apiVersion + "/graphql.json"; }

    function formatPrice(money) {
      if (!money) return "";
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency", currency: money.currencyCode,
        }).format(parseFloat(money.amount));
      } catch (e) {
        return money.currencyCode + " " + money.amount;
      }
    }

    function numericId(gid) { return String(gid || "").split("/").pop(); }

    function productUrl(handle, varId) {
      var u = rootUrl + "products/" + handle;
      return varId ? u + "?variant=" + varId : u;
    }

    function fetchProduct(handle) {
      if (productCache[handle]) return productCache[handle];
      var query =
        "query @inContext(country: " + country + ") { product(handle: " +
        JSON.stringify(handle) + ") { title featuredImage { url(transform:{maxWidth:640}) altText } " +
        "variants(first: 100) { nodes { sku title availableForSale id " +
        "price { amount currencyCode } image { url(transform:{maxWidth:640}) altText } " +
        "selectedOptions { name value } } } } }";
      var p = fetch(apiUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": token },
        body: JSON.stringify({ query: query }),
      })
        .then(function (r) { return r.json(); })
        .then(function (j) { return (j && j.data && j.data.product) || null; })
        .catch(function () { return null; });
      productCache[handle] = p;
      return p;
    }

    function el(tag, cls, text) {
      var e = document.createElement(tag);
      if (cls) e.className = cls;
      if (text != null) e.textContent = text;
      return e;
    }

    // Like el(), but the fabric weight ("11.8 oz/yd²") is wrapped in a .bcf__spec so it stands out.
    var SPEC_RE = /(\d+(?:\.\d+)?\s?oz\/yd²)/;
    function elSpec(tag, cls, text) {
      var e = el(tag, cls, null);
      String(text || "").split(SPEC_RE).forEach(function (part, i) {
        if (!part) return;
        e.appendChild(i % 2 ? el("strong", "bcf__spec", part) : document.createTextNode(part));
      });
      return e;
    }

    var SVG_NS = "http://www.w3.org/2000/svg";
    function tickIcon() {
      var svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("class", "bcf__tile-cta-tick");
      svg.setAttribute("viewBox", "0 0 20 20");
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
      var path = document.createElementNS(SVG_NS, "path");
      path.setAttribute("d", "M4 10.4l4 4 8-8.8");
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-width", "2.4");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      svg.appendChild(path);
      return svg;
    }

    // A tile can be picked by clicking anywhere on it, so the button has to say
    // which one is picked rather than sitting there inviting a second click:
    // selected reads as a tick and "Selected", unselected as the arrow prompt.
    function paintTileCta(cta, selected) {
      if (!cta) return;
      cta.innerHTML = "";
      cta.classList.toggle("is-selected", !!selected);
      cta.setAttribute("aria-pressed", selected ? "true" : "false");
      if (selected) {
        cta.appendChild(tickIcon());
        cta.appendChild(document.createTextNode(tileCtaSelectedLabel));
      } else {
        cta.appendChild(document.createTextNode(tileCtaLabel));
        var arrow = el("span", "bcf__tile-cta-arrow", "\u2192");
        arrow.setAttribute("aria-hidden", "true");
        cta.appendChild(arrow);
      }
    }

    // ---- population ----
    function populateManufacturers() {
      var names = Object.keys(DATA.manufacturers).sort();
      names.forEach(function (n) {
        var o = el("option", null, n);
        o.value = n;
        mfrSel.appendChild(o);
      });
    }

    // Model dropdown lists distinct baseModel values (not one row per config —
    // configs for a base model are disambiguated in the confirm-tiles step).
    function populateModels(mfr) {
      mdlSel.innerHTML = '<option value="">Select model…</option>';
      var recs = (DATA.manufacturers[mfr] || []);
      var groups = {};          // baseModel -> rows[]
      recs.forEach(function (r) { (groups[r.baseModel] = groups[r.baseModel] || []).push(r); });
      var bases = Object.keys(groups).sort();
      bases.forEach(function (b) {
        var o = el("option", null, b); o.value = b; mdlSel.appendChild(o);
      });
      mdlSel.disabled = bases.length === 0;
      mdlSel._groups = groups;
      tilesStep.hidden = true; tilesWrap.innerHTML = "";
      results.innerHTML = ""; emptyNote.hidden = true;
    }

    // ---- rendering ----
    function orderedCovers(rec) {
      var order = DATA.useCaseOrder || [];
      return rec.covers.slice().sort(function (a, b) {
        var ia = order.indexOf(a.useCase), ib = order.indexOf(b.useCase);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
    }

    var EYEBROW = {
      "Suggested cover": "Recommended",
      "With trolling motor / thruster": "Recommended · trolling motor / bimini down",
      "With bimini up": "Recommended · bimini permanently up",
      "Universal": "Universal",
      "Heavy duty winter cover": "Alternative · heavy duty winter storage",
      "Custom cover": "Custom fit",
    };

    function colorHex(name) {
      var n = (name || "").toLowerCase();
      if (n.indexOf("black") >= 0) return "#1c1f24";
      if (n.indexOf("grey") >= 0 || n.indexOf("gray") >= 0) return "#9aa3ad";
      if (n.indexOf("blue") >= 0) return "#1f4368";
      if (n.indexOf("white") >= 0) return "#eef0f2";
      if (n.indexOf("green") >= 0) return "#3a5a40";
      return "#8a93a3";
    }
    function isColorOpt(o) { return /colou?r/i.test(o.name); }
    function nonColorSig(v) {
      var so = v.selectedOptions || [], parts = [], i;
      for (i = 0; i < so.length; i++) { if (!isColorOpt(so[i])) parts.push(so[i].name + "=" + so[i].value); }
      return parts.sort().join("|");
    }
    function colorSiblings(variants, matched) {
      var mo = matched.selectedOptions || [], hasColor = false, i;
      for (i = 0; i < mo.length; i++) { if (isColorOpt(mo[i])) hasColor = true; }
      if (!hasColor) return null;
      var target = nonColorSig(matched), out = [];
      for (i = 0; i < variants.length; i++) {
        if (nonColorSig(variants[i]) !== target) continue;
        var cv = "", so = variants[i].selectedOptions || [], j;
        for (j = 0; j < so.length; j++) { if (isColorOpt(so[j])) cv = so[j].value; }
        if (cv) out.push({ name: cv, variant: variants[i] });
      }
      return out;
    }
    function fitLabel(variant, cover) {
      var so = variant.selectedOptions || [], parts = [], i, val;
      for (i = 0; i < so.length; i++) {
        if (isColorOpt(so[i])) continue;
        val = so[i].value;
        if (!val || val === "Default Title") continue; // Shopify placeholder for single-variant products
        parts.push(val);
      }
      if (parts.length) return "Fits boats " + parts.join(" · ");
      if (cover.useCase === "Custom cover") return "Made to measure for your boat";
      return "Size " + cover.code;
    }

    function buildCard(cover) {
      var card = el("div", "bcf__card bcf__card--loading");
      if (warrantyBadge) card.appendChild(warrantyBadge.cloneNode(true));
      var media = el("div", "bcf__card-media");
      // Per-cover-type corner badge (custom / classic / universal), top-left of the image.
      var group = COVER_GROUP[cover.useCase];
      var badgeUrl = group && BADGE_URLS[group];
      if (badgeUrl) {
        var bim = new Image();
        bim.src = badgeUrl;
        bim.alt = "";
        bim.className = "bcf__badge-img bcf__badge-img--" + group;
        media.appendChild(bim);
      }

      var body = el("div", "bcf__card-body");
      var eyebrow = el("p", "bcf__eyebrow", EYEBROW[cover.useCase] || cover.useCase);
      var title = el("h3", "bcf__card-title", cover.title || "Boat cover");
      var desc = elSpec("p", "bcf__card-desc", DESCRIPTIONS[cover.useCase] || "");
      var sizeEl = el("p", "bcf__size", "");
      var colorsWrap = el("div", "bcf__colors");
      var price = el("p", "bcf__price", "");
      var sku = el("p", "bcf__sku", "");
      sku.hidden = true;
      var actions = el("div", "bcf__actions");
      var view = el("a", "bcf__cta", "View cover");
      view.setAttribute("href", "#");
      actions.appendChild(view);

      var content = coverContent(cover.useCase);

      body.appendChild(eyebrow);
      body.appendChild(title);
      if (content && content.callout) body.appendChild(el("p", "bcf__callout", content.callout));
      if (desc.textContent) body.appendChild(desc);
      if (content && content.features) {
        var feats = splitLines(content.features);
        if (feats.length) {
          var ul = el("ul", "bcf__features");
          feats.forEach(function (line) {
            var li = el("li", "bcf__feature");
            var tk = el("span", "bcf__tick");
            tk.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
            li.appendChild(tk);
            li.appendChild(elSpec("span", "bcf__feature-text", line));
            ul.appendChild(li);
          });
          body.appendChild(ul);
        }
      }
      body.appendChild(sizeEl);
      if (cover.useCase === "Custom cover") {
        var disclaimer = el("p", "bcf__disclaimer");
        disclaimer.appendChild(el("strong", "bcf__disclaimer-label", "Disclaimer:"));
        disclaimer.appendChild(document.createTextNode(" This cover is made to fit the boat in its original factory configuration. Aftermarket accessories that increase the boat's length or dimensions may prevent a proper fit."));
        body.appendChild(disclaimer);
      }
      body.appendChild(colorsWrap);
      body.appendChild(price);
      body.appendChild(sku);
      body.appendChild(actions);

      var main = el("div", "bcf__card-main");
      main.appendChild(media);
      main.appendChild(body);
      card.appendChild(main);

      if (content && content.icons) {
        var badges = splitLines(content.icons);
        var iconFiles = splitLines(content.iconFiles);
        // Custom/classic covers always carry the Trailerable badge. Force it on
        // here (when missing) so saved instances render it too — schema-default
        // changes don't backfill section settings already stored in templates.
        if ((group === "custom" || group === "classic") && iconFiles.indexOf("trailer_bc.svg") === -1) {
          badges.push("Trailerable");
          iconFiles.push("trailer_bc.svg");
        }
        badges = badges.slice(0, 5);
        if (badges.length) {
          var strip = el("div", "bcf__card-features bcf__card-features--" + badges.length);
          badges.forEach(function (label, i) {
            var item = el("div", "bcf__feat");
            var badge = el("span", "bcf__feat-badge");
            var url = ICON_URLS[iconFiles[i]];
            if (url) {
              var im = new Image();
              im.loading = "lazy";
              im.src = url;
              im.alt = "";
              im.className = "bcf__feat-icon";
              badge.appendChild(im);
            } else {
              badge.appendChild(document.createTextNode(String(i + 1)));
            }
            item.appendChild(badge);
            item.appendChild(el("span", "bcf__feat-label", label));
            strip.appendChild(item);
          });
          card.appendChild(strip);
        }
      }

      if (cover.unresolved || !cover.handle) {
        renderEnquire(card, title, sizeEl, colorsWrap, price, view, cover);
        return card;
      }

      fetchProduct(cover.handle).then(function (product) {
        card.classList.remove("bcf__card--loading");
        if (!product) { // network/API failure: degrade to a plain PDP link
          colorsWrap.remove(); price.remove(); sizeEl.remove();
          view.setAttribute("href", productUrl(cover.handle, null));
          return;
        }
        title.textContent = product.title || cover.title || "Boat cover";
        var variants = (product.variants && product.variants.nodes) || [];
        var matched = null, i;
        for (i = 0; i < variants.length; i++) {
          if (variants[i].sku && variants[i].sku.trim().toUpperCase() === cover.code.toUpperCase()) { matched = variants[i]; break; }
        }
        if (!matched) { // listed product but this size not found: enquire
          renderEnquire(card, title, sizeEl, colorsWrap, price, view, cover);
          return;
        }
        var current = matched, addBtn = null;

        function paint(variant) {
          current = variant;
          var imgs = media.querySelectorAll("img.bcf__card-img"), k; // not the corner badge
          for (k = imgs.length - 1; k >= 0; k--) { imgs[k].parentNode.removeChild(imgs[k]); }
          var img = variant.image || product.featuredImage;
          if (img && img.url) {
            var im = new Image(); im.loading = "lazy"; im.src = img.url;
            im.alt = title.textContent; im.className = "bcf__card-img";
            media.insertBefore(im, media.firstChild);
          }
          sizeEl.textContent = fitLabel(variant, cover);
          if (variant.availableForSale) {
            price.textContent = formatPrice(variant.price);
            price.className = "bcf__price";
            if (addBtn) { addBtn.disabled = false; addBtn.textContent = "Add to cart"; }
          } else {
            price.textContent = "Currently unavailable";
            price.className = "bcf__price bcf__price--soldout";
            if (addBtn) addBtn.disabled = true;
          }
          if (variant.sku) { sku.textContent = "SKU: " + variant.sku; sku.hidden = false; }
          else { sku.hidden = true; }
          view.setAttribute("href", productUrl(cover.handle, numericId(variant.id)));
        }

        // colour swatches (only when a colour option exists)
        var sibs = colorSiblings(variants, matched);
        if (sibs && sibs.length > 1) {
          colorsWrap.appendChild(el("span", "bcf__colors-label", "Colour"));
          var row = el("div", "bcf__swatches");
          var swEls = [];
          for (i = 0; i < sibs.length; i++) {
            (function (sib) {
              var b = el("button", "bcf__swatch");
              b.type = "button";
              b.setAttribute("aria-label", sib.name);
              b.title = sib.name;
              var dot = el("span", "bcf__swatch-dot");
              dot.style.background = colorHex(sib.name);
              b.appendChild(dot);
              b.appendChild(el("span", null, sib.name));
              b.addEventListener("click", function () {
                for (var e = 0; e < swEls.length; e++) { swEls[e].classList.remove("is-selected"); }
                b.classList.add("is-selected");
                paint(sib.variant);
              });
              if (sib.variant.sku === matched.sku) b.classList.add("is-selected");
              swEls.push(b);
              row.appendChild(b);
            })(sibs[i]);
          }
          colorsWrap.appendChild(row);
        } else {
          colorsWrap.remove();
        }

        addBtn = el("button", "bcf__cta bcf__cta--ghost", "Add to cart");
        addBtn.type = "button";
        addBtn.addEventListener("click", function () {
          addBtn.disabled = true; addBtn.textContent = "Adding…";
          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: parseInt(numericId(current.id), 10), quantity: 1 }),
          })
            .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
            .then(function () { addBtn.textContent = "Added ✓"; refreshCart(); document.dispatchEvent(new CustomEvent("bcf:added")); setTimeout(function () { addBtn.disabled = false; }, 60); })
            .catch(function () { addBtn.textContent = "Add to cart"; addBtn.disabled = false; });
        });
        actions.appendChild(addBtn);

        paint(matched);
      });
      return card;
    }

    function renderEnquire(card, title, sizeEl, colorsWrap, price, view, cover) {
      card.classList.remove("bcf__card--loading");
      card.classList.add("bcf__card--enquire");
      if (colorsWrap && colorsWrap.parentNode) colorsWrap.remove();
      title.textContent = cover.useCase === "Custom cover" ? "Custom-made cover" : (cover.title || "Boat cover");
      sizeEl.textContent = "Made to order — code " + cover.code;
      price.remove();
      view.textContent = "Enquire";
      view.className = "bcf__cta bcf__cta--ghost";
      view.setAttribute("href", enquireUrl);
    }

    // The 3rd step is a confirm-tiles step: one tile per row (config) under the
    // chosen baseModel. A single row auto-advances straight to its covers.
    function renderCoversFor(rec) {
      results.innerHTML = "";
      var covers = orderedCovers(rec);
      if (!covers.length) {                       // no fitted cover for this config
        emptyNote.hidden = false;
        emptyNote.textContent = "No fitted cover for this configuration — please contact us and we'll help you find the right fit.";
        return;
      }
      emptyNote.hidden = true;
      covers.forEach(function (cover) { results.appendChild(buildCard(cover)); });
    }

    function renderTiles(base) {
      var rows = (mdlSel._groups && mdlSel._groups[base]) || [];
      tilesWrap.innerHTML = "";
      results.innerHTML = ""; emptyNote.hidden = true;
      var single = rows.length === 1;
      tilesWrap.classList.toggle("bcf__tiles--single", single);
      tilesStep.hidden = false;
      rows.forEach(function (rec) {
        var tile = el("div", "bcf__tile");
        // Boat illustration, when we have one for this config. Decorative: the
        // config label right underneath already names the boat, so an alt text
        // would only repeat it to a screen reader.
        // A row can name its diagram (`png`, from the team's master sheet), so a
        // boat-type change in the data doesn't swap the drawing; else pick one.
        var artFile = rec.png || (pickBoatArt ? pickBoatArt(rec) : null);
        var artUrl = artFile && BOAT_ART[artFile];
        if (artUrl) {
          var fig = el("div", "bcf__tile-art");
          var art = new Image();
          art.src = artUrl;
          art.alt = "";
          art.loading = "lazy";
          art.decoding = "async";
          art.width = 512; art.height = 217;
          art.className = "bcf__tile-art-img";
          fig.appendChild(art);
          tile.appendChild(fig);
        }
        // Text sits in its own growing box so the button lands on the same
        // line across a row of tiles however long the config labels run.
        var body = el("div", "bcf__tile-body");
        body.appendChild(el("div", "bcf__tile-config", rec.configLabel || rec.baseModel));
        if (rec.year) body.appendChild(el("div", "bcf__tile-year", rec.year));
        if (rec.boatType) body.appendChild(el("div", "bcf__tile-type", rec.boatType));
        tile.appendChild(body);
        if (!single) {
          // The whole tile is clickable; the button is what makes it reachable
          // by keyboard. It deliberately has no listener of its own — the click
          // it fires (mouse or Enter/Space) bubbles up to the tile handler, so
          // there is one code path either way.
          var cta = el("button", "bcf__cta bcf__tile-cta");
          cta.type = "button";
          paintTileCta(cta, false);
          tile.appendChild(cta);

          tile.addEventListener("click", function () {
            var sibs = tilesWrap.querySelectorAll(".bcf__tile"), k;
            for (k = 0; k < sibs.length; k++) {
              sibs[k].classList.remove("is-selected");
              paintTileCta(sibs[k].querySelector(".bcf__tile-cta"), false);
            }
            tile.classList.add("is-selected");
            paintTileCta(cta, true);
            renderCoversFor(rec);
          });
        }
        tilesWrap.appendChild(tile);
      });
      if (single) { tilesWrap.firstChild.classList.add("is-selected"); renderCoversFor(rows[0]); }
    }

    // ---- wire up ----
    mfrSel.addEventListener("change", function () {
      tilesStep.hidden = true; results.innerHTML = ""; emptyNote.hidden = true;
      if (!mfrSel.value) { mdlSel.innerHTML = '<option value="">Select model…</option>'; mdlSel.disabled = true; return; }
      populateModels(mfrSel.value);
    });
    mdlSel.addEventListener("change", function () {
      if (!mdlSel.value) { tilesStep.hidden = true; results.innerHTML = ""; return; }
      renderTiles(mdlSel.value);
    });

    if (!dataSrc) return;
    fetch(dataSrc)
      .then(function (r) { return r.json(); })
      .then(function (json) { DATA = json; populateManufacturers(); })
      .catch(function () {
        emptyNote.hidden = false;
        emptyNote.textContent = "Sorry — the boat cover finder could not load. Please refresh or contact us.";
      });
  }

  function boot() {
    document.querySelectorAll(".bcf--us").forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
